<script lang="ts">
import { computed, shallowRef, watch } from 'vue';
import { Data, OmsOrderStatus, OrderIntent, RateType, Side, create } from '@/connect';
import {
  FrontendOrder,
  QueryOrdersFilterSchema,
} from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import { marketplaceService } from '@/connect/services/marketplace';
import { useRateTypeStore } from '@/store/rate-type-store';
import { useSecuritiesStore } from '@/store/securities-store';
import formatPrettyNumber from '@/modules/common/components/pretty-number/formatPrettyNumber';
import RateOutput from '@/modules/common/components/format-rate/RateOutput.vue';
import { BookOrder } from '@/connect/gen/modules/apiengine/services/venue/venue_pb';
import { getPriceAsString } from '@/utils/helpers/auction-numbers';
import { PRICE_PRECISION } from '@/modules/common/constants/precision';
import Decimal from 'decimal.js';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const OrderSelection = {
  NO_PREFERENCE: 0,
  NEW_ORDER: 1,
} as const;
export type OrderSelection = (typeof OrderSelection)[keyof typeof OrderSelection];
</script>

<script setup lang="ts">
const rateTypeStore = useRateTypeStore();
const securitiesStore = useSecuritiesStore();

const props = defineProps<{
  value: Data<FrontendOrder> | OrderSelection;
  orderToMatch: BookOrder | null;
}>();

const emit = defineEmits<{ (event: 'input', value: Data<FrontendOrder> | OrderSelection): void }>();

const myOrderSelected = computed({
  get: () => props.value,
  set: (value) => emit('input', value ?? OrderSelection.NEW_ORDER),
});

const myOrders = shallowRef<Array<Data<FrontendOrder>>>([]);
const myOrderOptions = computed(() => [
  { text: 'New Order', value: OrderSelection.NEW_ORDER },
  ...myOrders.value.map((order) => {
    const rate = new Decimal(
      (rateTypeStore.marketplaceRateType === RateType.REBATE ? order.rebate : order.fee) ?? '0'
    ).toFixed(4);
    return {
      text: `${formatOrderIntent(order.orderIntent)}: ${`${formatPrettyNumber(order.openQuantity)} @ ${rate}%`}`,
      value: order,
    };
  }),
]);

const orderValue = computed(() => {
  if (typeof myOrderSelected.value !== 'object' || !props.orderToMatch) return null;
  return (
    Number(myOrderSelected.value.openQuantity) *
    Number(securitiesStore.getSecurity(props.orderToMatch.cusip)?.lastClosePrice)
  );
});

function formatPrice(price: number): string {
  return getPriceAsString(price, PRICE_PRECISION);
}

function formatOrderIntent(orderIntent: OrderIntent): string {
  return OrderIntent[orderIntent] === 'IOI' ? 'IOI' : 'Firm';
}

const controller = new AbortController();
watch(() => props.orderToMatch, refreshMyOrders, { immediate: true });

async function refreshMyOrders() {
  const order = props.orderToMatch;
  controller.abort();
  if (!order) {
    myOrders.value = [];
    return;
  }
  const result = await marketplaceService.queryOrders({
    page: 1,
    pageSize: 100,
    signal: controller.signal,
    sort: { colId: 'updatedAt', sort: 'desc' },
    filter: create(QueryOrdersFilterSchema, {
      side: { type: 'equals', filter: order.side === Side.BORROW ? Side.LOAN : Side.BORROW },
      cusip: { type: 'equals', filter: order.cusip },
      orderStatus: {
        type: 'equals',
        values: [{ orderStatus: OmsOrderStatus.OPEN }],
      },
      offsetInMinutesFromUtc: new Date().getTimezoneOffset(),
    }),
  });

  if (result.success) {
    myOrders.value = result.data.orders;
    const sortModifier = order.side === Side.BORROW ? 1 : -1;
    myOrders.value.sort(
      (a, b) => (Number(a.rebate) - Number(b.rebate)) * sortModifier || a.orderType - b.orderType
    );
    if (
      myOrderSelected.value === OrderSelection.NO_PREFERENCE ||
      (myOrderSelected.value !== OrderSelection.NEW_ORDER &&
        !myOrders.value.includes(myOrderSelected.value))
    ) {
      myOrderSelected.value = myOrders.value[0] ?? OrderSelection.NEW_ORDER;
    }
  }
}

defineExpose({
  refreshMyOrders,
});
</script>

<template>
  <div>
    <VSelect
      v-if="myOrders.length"
      v-model="myOrderSelected"
      :items="myOrderOptions"
      label="From Order"
    >
      <template #item="{ item: { value: order } }">
        <div v-if="order === OrderSelection.NEW_ORDER" class="pa-2">New Order</div>
        <div v-else class="pa-2">
          <div>Quantity: {{ formatPrettyNumber(order.openQuantity) }}</div>
          <div v-if="rateTypeStore.marketplaceRateType === RateType.REBATE">
            Rebate Rate:
            <RateOutput :rate="order.rebate" />
          </div>
          <div v-else>
            Fee:
            <RateOutput :rate="order.fee" />
          </div>
          <div>Order Intent: {{ formatOrderIntent(order.orderIntent) }}</div>
        </div>
      </template>
    </VSelect>

    <VAlert
      v-if="typeof myOrderSelected === 'object'"
      class="mt-4 order-info mb-0"
      color="grey"
      dense
      outlined
    >
      <div><label>Quantity:</label> {{ formatPrettyNumber(myOrderSelected.openQuantity) }}</div>
      <div v-if="orderValue"><label>Value:</label> ${{ formatPrice(orderValue) }}</div>
      <div v-if="rateTypeStore.marketplaceRateType === RateType.REBATE && myOrderSelected.rebate">
        <label>Rebate Rate:</label> <RateOutput :rate="myOrderSelected.rebate" />
      </div>
      <div v-else-if="myOrderSelected.fee">
        <label>Fee:</label>
        <RateOutput :rate="myOrderSelected.fee" />
      </div>
      <div><label>Order Intent:</label> {{ formatOrderIntent(myOrderSelected.orderIntent) }}</div>
    </VAlert>
  </div>
</template>

<style lang="scss" scoped>
.order-info label {
  width: 6rem;
  display: inline-block;
}
</style>
